import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdKeyboardArrowDown } from 'react-icons/md';

/** How far off the bottom counts as "reading history" rather than "at the end". */
const AWAY_PX = 240;

/**
 * Jump back to the newest message.
 *
 * Appears only while the reader is genuinely away from the bottom of the stream,
 * and carries a count of what has arrived since — which is the part that makes it
 * worth having: scrolling up in a busy conversation previously meant either
 * missing new messages entirely or dragging back down to find out.
 *
 * `scrollRef` is the scroll container (`.pane-stream`); `newestKey` is any value
 * that changes when a message arrives — the message count works. The pill counts
 * changes to that key while away rather than inspecting messages, so it needs to
 * know nothing about their shape.
 */
const ScrollToLatest = ({ scrollRef, newestKey }) => {
  const [away, setAway] = useState(false);
  const [missed, setMissed] = useState(0);

  // Read inside the arrival effect without making it depend on `away` — that
  // dependency would re-run it on every scroll across the threshold and count
  // arrivals that never happened.
  const awayRef = useRef(false);
  awayRef.current = away;

  useEffect(() => {
    const node = scrollRef?.current;
    if (!node) return undefined;

    const onScroll = () => {
      const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
      const isAway = distance > AWAY_PX;
      setAway(isAway);
      if (!isAway) setMissed(0);
    };

    onScroll();
    node.addEventListener('scroll', onScroll, { passive: true });
    return () => node.removeEventListener('scroll', onScroll);
  }, [scrollRef]);

  // A new message while away is one the reader has not seen.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (awayRef.current) setMissed((n) => n + 1);
  }, [newestKey]);

  const jump = useCallback(() => {
    const node = scrollRef?.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
    setMissed(0);
  }, [scrollRef]);

  if (!away) return null;

  return (
    <button type="button" className="stream-jump" onClick={jump} aria-label="Jump to latest">
      <MdKeyboardArrowDown size={20} />
      {missed > 0 && <span className="stream-jump-count">{missed > 99 ? '99+' : missed}</span>}
    </button>
  );
};

export default ScrollToLatest;
